'use client';

import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import Image from 'next/image';

import { transformedImageUrl } from '~/lib/image-cdn';
import { safeLocalStorage } from '~/lib/safe-storage';
import { STORY_AVATARS } from '~/lib/story-avatars';

import type { Story } from './story-viewer';

// The full-screen viewer (portal + react-query + spark actions, ~530 lines)
// only mounts on tap. Splitting it out keeps its weight off the landing
// bundle's first load — it downloads when the user actually opens a story.
const StoryViewer = dynamic(() =>
  import('./story-viewer').then((m) => m.StoryViewer),
);

/** Per-character framing for the story-circle avatars (their source photos
 * sit differently in frame). objectPosition tunes the visible slice; a small
 * scale <1 zooms out to reveal more of the subject. */
const AVATAR_FRAMING: Record<string, { pos?: string }> = {
  'bianca-costa': { pos: '50% 78%' }, // she sits low in her photo — focus down
  'margot-laurent': { pos: '50% 32%' }, // frame slightly higher
  'mei-lin': { pos: '50% 32%' },
};

const WATCHED_KEY = 'edem:watched-stories';

function loadWatched(): Set<string> {
  try {
    const raw = safeLocalStorage.getItem(WATCHED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/**
 * "Choose Your Spark" — a row of featured characters as Instagram-style
 * circles. Tapping one opens the full-screen story viewer with a random
 * selection of that character's free media (images + videos). Watched
 * characters fade their ring to gray (persisted in localStorage).
 */
export function StoriesRow({ stories }: { stories: Story[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [watched, setWatched] = useState<Set<string>>(() => new Set());

  // Hydrate the watched set from localStorage on mount (client only).
  useEffect(() => {
    setWatched(loadWatched());
  }, []);

  // Mark the currently-open character as watched + persist it.
  useEffect(() => {
    if (activeIndex === null) return;
    const id = stories[activeIndex]?.id;
    if (!id) return;
    setWatched((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      safeLocalStorage.setItem(WATCHED_KEY, JSON.stringify([...next]));
      return next;
    });
  }, [activeIndex, stories]);

  if (stories.length === 0) return null;

  return (
    <section className="relative z-10 pt-6 pb-0 sm:pt-8">
      <div className="mx-auto px-3 sm:px-4" style={{ maxWidth: '1400px' }}>
        <div className="flex gap-4 overflow-x-auto pb-2 sm:gap-6 [&::-webkit-scrollbar]:hidden">
          {stories.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActiveIndex(i)}
              className="group flex flex-shrink-0 flex-col items-center gap-2"
            >
              <span
                className="relative rounded-full p-[3px]"
                style={{
                  background: watched.has(s.id)
                    ? 'linear-gradient(to top right, #4b5563, #6b7280)'
                    : 'linear-gradient(to top right, #f472b6, #ec4899, #db2777)',
                }}
              >
                <Image
                  src={
                    transformedImageUrl(
                      STORY_AVATARS[s.slug] ?? s.avatarUrl,
                      160,
                    ) ?? '/images/favicon/logo.webp'
                  }
                  alt={s.name.split(' ')[0] ?? s.name}
                  width={80}
                  height={80}
                  sizes="80px"
                  className="h-16 w-16 rounded-full border-2 border-black object-cover transition-transform group-hover:scale-105 sm:h-20 sm:w-20"
                  style={{
                    objectPosition: AVATAR_FRAMING[s.slug]?.pos ?? '50% 20%',
                  }}
                  // Already-AVIF CDN media — skip Vercel's redundant re-encode.
                  unoptimized
                />
              </span>
              <span className="max-w-[6rem] truncate text-center text-sm font-medium text-white">
                {s.name.split(' ')[0]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {activeIndex !== null && (
        <StoryViewer
          stories={stories}
          index={activeIndex}
          onIndexChange={setActiveIndex}
          onClose={() => setActiveIndex(null)}
        />
      )}
    </section>
  );
}
